import { useRouteError, isRouteErrorResponse, Link } from 'react-router';
import { AlertCircle, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

export function ErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();

  // Log error to console for debugging
  useEffect(() => {
    console.error('Route Error:', error);
  }, [error]);

  if (isRouteErrorResponse(error)) {
    console.log('Route Error Response:', {
      status: error.status,
      statusText: error.statusText,
      data: error.data,
    });
    
    if (error.status === 404) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-md">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-orange-600" />
              </div>
            </div>
            <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {t('errors.pageNotFound', 'Page Not Found')}
            </h2>
            <p className="text-gray-600 mb-8">
              {t('errors.pageNotFoundMessage', 'The page you are looking for does not exist or has been moved.')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Home className="w-5 h-5" />
              {t('errors.backToHome', 'Back to Home')}
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
          </div>
          <h1 className="text-6xl font-bold text-gray-900 mb-4">{error.status}</h1>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {error.statusText}
          </h2>
          {error.data && (
            <p className="text-gray-600 mb-8">{error.data}</p>
          )}
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Home className="w-5 h-5" />
            {t('errors.backToHome', 'Back to Home')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        <div className="mb-6 flex justify-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          {t('errors.unexpectedError', 'Unexpected Error')}
        </h1>
        <p className="text-gray-600 mb-2">
          {t('errors.somethingWentWrong', 'Something went wrong. Please try again.')}
        </p>
        {error instanceof Error && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              {t('errors.technicalDetails', 'Technical Details')}
            </summary>
            <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Home className="w-5 h-5" />
            {t('errors.backToHome', 'Back to Home')}
          </Link>
        </div>
      </div>
    </div>
  );
}